// src/api/auctionLotApi.ts
import client from "./client";

const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

export type AuctionLotStatus = "PENDIENTE" | "ACTIVO" | "CERRADO" | "CANCELADO";

export interface AuctionLot {
  id: string;
  nroLote: number;
  orden: number;
  videoUrl: string | null;
  cantAnimales: number;
  tipoGanado: string | null;
  pesoProm: number | null;
  pesoTotal: number | null;
  ubicacion: string | null;
  precioMinimo: number;
  valorKg: number | null;
  valorPorAnimal: number | null;
  observaciones: string | null;
  status: AuctionLotStatus;
  paletaGanadora: number | null;
  usuarioGanadorId: string | null;
  montoGanador: number | null;
  publishedId: string | null;
  currentBid?: number | null;
  bidCount?: number | null;
  incrementoPuja?: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface LotEventDTO {
  type: "LOT_ACTIVATED" | "LOT_CLOSED" | "LOT_REGISTERED";
  lotId: string;
  nroLote: number;
  orden: number;
  videoUrl: string | null;
  cantAnimales: number;
  tipoGanado: string | null;
  pesoProm: number | null;
  pesoTotal: number | null;
  ubicacion: string | null;
  precioMinimo: number;
  valorKg: number | null;
  valorPorAnimal: number | null;
  incrementoPuja?: number | null;
  status: AuctionLotStatus;
  paletaGanadora: number | null;
  usuarioGanadorId: string | null;
  montoGanador: number | null;
  message: string | null;
  serverTime: string;
}

export interface CreateLotPayload {
  video?: File | null;
  onUploadProgress?: (pct: number) => void;
  cantAnimales: number;
  tipoGanado?: string;
  pesoProm?: number;
  pesoTotal?: number;
  ubicacion?: string;
  precioMinimo?: number;
  valorKg?: number;
  valorPorAnimal?: number;
  observaciones?: string;
  orden?: number;
}

export interface AssignWinnerPayload {
  paletaGanadora: number;
  montoGanador?: number;
}

export interface LotBidAdmin {
  bidId: string;
  palletNumber: number;
  amount: number;
  createdAt: string;
  userId: string | null;
  userName: string;
  userEmail: string | null;
  esUltimaOferta: boolean;
}

export interface LotParticipant {
  id: string;
  userId: string;
  userName: string;
  userEmail: string | null;
  userPhone: string | null;
  numeroPaleta: number | null;
  aprobado: boolean;
  fechaAprobacion: string | null;
  createdAt: string | null;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export async function crearLote(payload: CreateLotPayload): Promise<AuctionLot> {
  const form = new FormData();
  if (payload.video) form.append("video", payload.video);
  form.append("cantAnimales", String(payload.cantAnimales));
  if (payload.tipoGanado) form.append("tipoGanado", payload.tipoGanado);
  if (payload.pesoProm != null) form.append("pesoProm", String(payload.pesoProm));
  if (payload.pesoTotal != null) form.append("pesoTotal", String(payload.pesoTotal));
  if (payload.ubicacion) form.append("ubicacion", payload.ubicacion);
  if (payload.precioMinimo != null) form.append("precioMinimo", String(payload.precioMinimo));
  if (payload.valorKg != null) form.append("valorKg", String(payload.valorKg));
  if (payload.valorPorAnimal != null) form.append("valorPorAnimal", String(payload.valorPorAnimal));
  if (payload.observaciones) form.append("observaciones", payload.observaciones);
  if (payload.orden != null) form.append("orden", String(payload.orden));
  const res = await client.post("/admin/lots", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: payload.video ? UPLOAD_TIMEOUT_MS : 15000,
    onUploadProgress: payload.onUploadProgress
      ? (e) => { if (e.total) payload.onUploadProgress!(Math.round((e.loaded * 100) / e.total)); }
      : undefined,
  });
  return res.data.content;
}

export async function actualizarLote(id: string, payload: Partial<CreateLotPayload>): Promise<AuctionLot> {
  const form = new FormData();
  if (payload.video) form.append("video", payload.video);
  if (payload.cantAnimales != null) form.append("cantAnimales", String(payload.cantAnimales));
  if (payload.tipoGanado) form.append("tipoGanado", payload.tipoGanado);
  if (payload.pesoProm != null) form.append("pesoProm", String(payload.pesoProm));
  if (payload.pesoTotal != null) form.append("pesoTotal", String(payload.pesoTotal));
  if (payload.ubicacion) form.append("ubicacion", payload.ubicacion);
  if (payload.precioMinimo != null) form.append("precioMinimo", String(payload.precioMinimo));
  if (payload.valorKg != null) form.append("valorKg", String(payload.valorKg));
  if (payload.valorPorAnimal != null) form.append("valorPorAnimal", String(payload.valorPorAnimal));
  if (payload.observaciones) form.append("observaciones", payload.observaciones);
  if (payload.orden != null) form.append("orden", String(payload.orden));
  const res = await client.put(`/admin/lots/${id}/video`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: payload.video ? UPLOAD_TIMEOUT_MS : 15000,
    onUploadProgress: payload.onUploadProgress
      ? (e) => { if (e.total) payload.onUploadProgress!(Math.round((e.loaded * 100) / e.total)); }
      : undefined,
  });
  return res.data.content;
}

export async function activarLote(id: string): Promise<AuctionLot> {
  const res = await client.post(`/admin/lots/${id}/activate`);
  return res.data.content;
}

export async function activarSiguienteLote(): Promise<AuctionLot> {
  const res = await client.post("/admin/lots/next");
  return res.data.content;
}

export async function cerrarLotePorPuja(lotId: string, bidId: string): Promise<AuctionLot> {
  const res = await client.post(`/admin/lots/${lotId}/close-by-bid`, { bidId });
  return res.data.content;
}

export async function cerrarLoteConGanador(id: string, payload: AssignWinnerPayload): Promise<AuctionLot> {
  const res = await client.post(`/admin/lots/${id}/close`, payload);
  return res.data.content;
}

export async function listarLotes(status?: AuctionLotStatus): Promise<AuctionLot[]> {
  const res = await client.get("/admin/lots", { params: status ? { status } : {} });
  return res.data.content ?? [];
}

export async function obtenerLoteActivo(): Promise<AuctionLot | null> {
  const res = await client.get("/admin/lots/active");
  return res.data.content ?? null;
}

export async function obtenerLotePorId(id: string): Promise<AuctionLot> {
  const res = await client.get(`/admin/lots/${id}`);
  return res.data.content;
}

export async function getBidsAdmin(lotId: string): Promise<LotBidAdmin[]> {
  const res = await client.get(`/admin/lots/${lotId}/bids`);
  return res.data.content ?? [];
}

export async function getParticipants(lotId: string): Promise<LotParticipant[]> {
  const res = await client.get(`/admin/lots/${lotId}/participants`);
  return res.data.content ?? [];
}

export async function approveParticipant(lotId: string, userId: string): Promise<LotParticipant> {
  const res = await client.post(`/admin/lots/${lotId}/participants/${userId}/approve`);
  return res.data.content;
}

export async function revokeParticipant(lotId: string, userId: string): Promise<LotParticipant> {
  const res = await client.post(`/admin/lots/${lotId}/participants/${userId}/revoke`);
  return res.data.content;
}

// ─── UTILIDADES ───────────────────────────────────────────────────────────────

export const formatCOP = (n?: number | null): string => {
  if (n == null) return "$0";
  try {
    return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(n);
  } catch {
    return `$${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  }
};

export const formatDateEs = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  try {
    return `${d.toLocaleDateString("es-CO", { day: "2-digit", month: "long", year: "numeric" })} - ${d.toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit" })}`;
  } catch { return d.toISOString(); }
};

export const statusLabel: Record<AuctionLotStatus, string> = {
  PENDIENTE: "Pendiente", ACTIVO: "Activo", CERRADO: "Cerrado", CANCELADO: "Cancelado",
};

export const statusColor: Record<AuctionLotStatus, string> = {
  PENDIENTE: "#f98115", ACTIVO: "#0cbb1d", CERRADO: "#B3B3B3", CANCELADO: "#D60D4C",
};

// ─── SUBASTA CONFIG ───────────────────────────────────────────────────────────

export interface SubastaConfig {
  promoVideoUrl: string | null;
  promoActivo: boolean;
  streamType?: string | null;
  // ── Mux ──────────────────────────────────────────────────────────────────
  muxStreamId?: string | null;
  muxRtmpUrl?: string | null;
  muxStreamKey?: string | null;
  muxPlaybackId?: string | null;
  /** URL HLS (.m3u8) — reproducir con hls.js */
  muxPlaybackUrl?: string | null;
  /** "idle" | "active" | "disconnected" */
  muxStatus?: string | null;
}

// ── Mux ────────────────────────────────────────────────────────────────────────

/**
 * Crea un nuevo Live Stream en Mux.
 * Devuelve: muxRtmpUrl, muxStreamKey, muxPlaybackUrl para configurar OBS.
 */
export async function crearMuxStream(): Promise<{
  muxRtmpUrl: string;
  muxStreamKey: string;
  muxPlaybackUrl: string;
  muxPlaybackId: string;
  muxStreamId: string;
  status: string;
}> {
  const res = await client.post("/admin/subasta-config/mux/create-stream");
  return res.data;
}

/**
 * Consulta el estado actual del stream Mux desde la BD.
 * Endpoint público. Usado como poll de respaldo.
 */
export async function refreshMuxStatus(): Promise<{
  status: string;
  muxPlaybackUrl: string;
  muxPlaybackId?: string;
}> {
  try {
    const res = await client.get("/admin/subasta-config/mux/status");
    return res.data;
  } catch {
    return { status: "unknown", muxPlaybackUrl: "" };
  }
}

/**
 * Fuerza el estado del stream Mux manualmente.
 * Útil cuando el webhook no llega a tiempo.
 */
export async function forzarEstadoMux(status: "active" | "idle" | "disconnected"): Promise<void> {
  await client.post("/admin/subasta-config/mux/set-status", { status });
}

// ── Compartidas ────────────────────────────────────────────────────────────────

export async function getSubastaConfig(): Promise<SubastaConfig> {
  const res = await client.get("/admin/subasta-config");
  return res.data;
}

export async function updateSubastaConfig(
  payload: Partial<Pick<SubastaConfig, "promoVideoUrl" | "promoActivo">>,
): Promise<SubastaConfig> {
  const res = await client.put("/admin/subasta-config", payload);
  return res.data;
}

export async function togglePromoVideo(activo: boolean): Promise<void> {
  await client.post("/admin/subasta-config/promo", { activo });
}
